import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { RolloutPlanStore } from '../../../+state/rollout/rollout-plan-form.store';
import { useRolloutPlanStore } from '../../../+state/rollout/rollout-plan.store';
import { ProductGroupsDto } from '../../../infrastructure/backends/rollout-plan.dto';

import { TargetProductGroupsService } from './target-product-groups.service';

interface ProductGroupSelection {
  id: number;
  selectedProductGroup: ProductGroupsDto | null;
}

@Component({
  selector: 'remote-update-domain-app-target-product-groups',
  templateUrl: './target-product-groups.html',
  styleUrls: ['./target-product-groups.css'],
  imports: [CommonModule, MatFormFieldModule, MatSelectModule, MatInputModule, FormsModule]
})
export class TargetProductGroups implements OnInit {
  @Input() currentSection = '';
  private store = inject(useRolloutPlanStore);
  private rolloutPlanGlobalStore = inject(RolloutPlanStore);
  private targetProductGroupsService = inject(TargetProductGroupsService);

  groupProducts: ProductGroupsDto[] = [];
  selectionInputs = signal<ProductGroupSelection[]>([{ id: 1, selectedProductGroup: null }]);
  allSelectedProductGroups = signal<{ name: string; productGroupId: number; inputIndex: number }[]>(
    []
  );
  isEditingRolloutPlan = computed(() => this.rolloutPlanGlobalStore.isEditingRolloutPlan());

  rolloutPlanStatus = computed(() => this.rolloutPlanGlobalStore.rolloutPlanStatusInEdit());

  isProductGroupsValid = computed(() => {
    const inputs = this.selectionInputs();
    const selected = this.allSelectedProductGroups();

    return selected.length > 0 && selected.length === inputs.length;
  });

  get selectedProductGroups(): ProductGroupsDto[] {
    return this.selectionInputs()
      .map(selection => selection.selectedProductGroup)
      .filter(Boolean) as ProductGroupsDto[];
  }
  private nextId = 2;

  ngOnInit() {
    this.fetchProductGroups();
    if (this.isEditingRolloutPlan()) {
      this.initializeSelectionsFromRolloutPlanOnEdit();
    }
  }

  constructor() {
    effect(() => {
      const isValid = this.isProductGroupsValid();
      this.rolloutPlanGlobalStore.updateProductGroups(
        {
          productGroups: isValid ? this.allSelectedProductGroups() : [],
          isValid: isValid ? true : false
        },
        ''
      );
    });
  }

  private initializeSelectionsFromRolloutPlanOnEdit() {
    const storedGroups = this.rolloutPlanGlobalStore.productGroups().productGroups;
    if (!storedGroups.length) return;

    const selections: ProductGroupSelection[] = storedGroups.map(productGroup => ({
      id: productGroup.inputIndex,
      selectedProductGroup: {
        id: productGroup.productGroupId,
        name: productGroup.name
      }
    }));

    this.selectionInputs.set(selections);

    this.allSelectedProductGroups.set(
      storedGroups.map(productGroup => ({
        inputIndex: productGroup.inputIndex,
        productGroupId: productGroup.productGroupId,
        name: productGroup.name
      }))
    );

    // Keeping ID counter correct
    this.nextId = Math.max(...storedGroups.map(productGroup => productGroup.inputIndex)) + 1;
  }

  isProductGroupDisabled(group: ProductGroupsDto, currentInputId: number): boolean {
    return this.selectionInputs().some(
      selection =>
        selection.id !== currentInputId && selection.selectedProductGroup?.id === group.id
    );
  }

  async fetchProductGroups() {
    await this.targetProductGroupsService.loadProductGroups();

    const productGroups = this.store.allProductGroups().data;

    this.groupProducts = [...(productGroups ?? [])];
  }

  trackById(_index: number, item: ProductGroupSelection) {
    return item.id;
  }

  addSelectionInput() {
    this.selectionInputs.update(inputs => [
      ...inputs,
      { id: this.nextId++, selectedProductGroup: null }
    ]);
  }

  removeSelectionInput(index: number) {
    this.selectionInputs.update(inputs => {
      if (index === 0) return inputs;

      const removedId = inputs[index].id;

      this.allSelectedProductGroups.update(arr =>
        arr.filter(productGroup => productGroup.inputIndex !== removedId)
      );

      return inputs.filter((_, i) => i !== index);
    });
  }

  onProductGroupChange(value: string, productGroupId: number, inputIndex: number) {
    this.allSelectedProductGroups.update(currentSelections => {
      // remove previous value for THIS input
      const others = currentSelections.filter(selection => selection.inputIndex !== inputIndex);

      return [...others, { name: value, productGroupId, inputIndex }];
    });
  }

  get error(): string | undefined {
    return this.rolloutPlanGlobalStore.errors().targetProductGroups;
  }
  compareProductGroups = (selected: ProductGroupsDto | null, option: ProductGroupsDto | null) =>
    selected?.id === option?.id;

  isInputInvalid(selection: ProductGroupSelection): boolean {
    // show error only when:
    // 1. there is an extra empty input
    // 2. this input has no selected value
    return !!this.error && !selection.selectedProductGroup;
  }

  canModifyTargets(): boolean {
    const status = this.rolloutPlanStatus();
    return status === 'Draft' || status === 'Ready for review' || status === '';
  }
}
